'use strict';

// external imports
import React, {
    useCallback,
    useMemo
} from 'react';
import {useSelector} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';

// frontend imports
import {StoreState} from '~/frontend/src/store';
import {ComponentProps as TargetComponentProps} from '~/frontend/src/modules/panels/XmlImport';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

// action imports


export type DefaultProps = {

    /**
     * Show the upload button in the toolbar.
     */
    showUploadButton? : boolean;

    /**
     * The error count to show as badge on upload button.
     *
     * Only applicable if <prop>showUploadButton</prop> is set to true.
     */
    uploadButtonErrorCount? : number;

    /**
     * The error tooltip to show on upload button.
     *
     * Only applicable if <prop>showUploadButton</prop> is set to true AND <prop>uploadButtonErrorCount</prop> is set to a positive integer.
     */
    uploadButtonErrorTooltip? : string | JSX.Element | ( string | JSX.Element )[];

    /**
     * Show the panel buttons in the toolbar.
     */
    showPanelButtons? : boolean;

    /**
     * The active panel for panel buttons.
     *
     * Only applicable if <prop>showPanelButtons</prop> is set to true.
     */
    activePanel? : TargetComponentProps['activePanel'];
};
export const defaultProps : Required<DefaultProps> = {
    showUploadButton        : true,
    uploadButtonErrorCount  : 0,
    uploadButtonErrorTooltip: '',
    showPanelButtons        : true,
    activePanel             : 'fileUpload'
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    onUpload : () => void;
    onActivePanelChange : ( panel : TargetComponentProps[ 'activePanel' ] ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = DefaultProps & Props;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const {
              uid,
              showUploadButton,
              uploadButtonErrorCount,
              uploadButtonErrorTooltip,
              showPanelButtons,
              activePanel,
              onUpload,
              onActivePanelChange
          }                            = props,
          user                         = useSelector( ( state : StoreState ) => state.session?.user ),
          language                     = user ? user.language : undefined,
          onUploadButtonClick          = useCallback(
              () => {
                  onUpload();
              },
              [
                  onUpload
              ]
          ),
          onFileUploadPanelButtonClick = useCallback(
              () => {
                  onActivePanelChange( 'fileUpload' );
              },
              [
                  onActivePanelChange
              ]
          ),
          onInputPanelButtonClick      = useCallback(
              () => {
                  onActivePanelChange( 'input' );
              },
              [
                  onActivePanelChange
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showUploadButton ) {
                layoutComponents.left.push( {
                    type                  : 'saveButton',
                    saveButtonErrorCount  : uploadButtonErrorCount,
                    saveButtonErrorTooltip: uploadButtonErrorTooltip,
                    params                : {
                        key: `${uid}_uploadButton`,

                        icon   : 'pi pi-upload',
                        tooltip: __( 'YN.base.upload.button' ),
                        onClick: onUploadButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showPanelButtons ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_panelButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_inputPanelButton`,

                                    label    : __( 'YN.base.xmlImport.input.label' ),
                                    onClick  : onInputPanelButtonClick,
                                    className: activePanel === 'input' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_fileUploadPanelButton`,

                                    label    : __( 'YN.base.xmlImport.fileUpload.label' ),
                                    onClick  : onFileUploadPanelButtonClick,
                                    className: activePanel === 'fileUpload' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            // right

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                left={toolbarComponents.left}
                right={toolbarComponents.right}
                className="yn-panel-toolbar yn-toolbar-horizontal-separator-bottom"
                style={{ zIndex: 6 }}
            />;
        },
        [
            uid,
            showUploadButton,
            uploadButtonErrorCount,
            uploadButtonErrorTooltip,
            showPanelButtons,
            activePanel,
            onUploadButtonClick,
            onFileUploadPanelButtonClick,
            onInputPanelButtonClick,
            language
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'XmlImport-Toolbar';

export default Toolbar;
